import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import { required, maxLength, decimal, numeric, between } from 'vuelidate/lib/validators'
import { SectionsProvider } from '@/content/providers/sections.provider';
import { CreateObjectsMixin } from '@/courses/mixins/create-objects.mixin';
import { Component, Mixins, Prop, Ref } from 'vue-property-decorator';
import { Section } from '@/courses/models/section.model';
import { SECTION_TYPES } from '@/content/values/sections';
import { IParamsCourseObject } from '@/courses/interfaces/params-course-object.interface';
import EdCollaborativeText from '@/components/common/ed-collaborative-text-editor/ed-collaborative-text.vue';
import { TextMixin } from '@/components/common/ed-collaborative-text-editor/ed-text.mixin';
import {Delta} from 'quill';

@Component({
  name: 'ed-create-text-object',
  components: {
    EdSelectTag: () => import(/* EdSelectTag */'@/courses/components/ed-select-tag/ed-select-tag.component.vue')
    ,EdCollaborativeText,
  }
})
export default class EdCreateTextObject extends Mixins(CreateObjectsMixin, TextMixin) {

  //@Prop() readonly params: IParamsCourseObject;    //Typescript upgrade
  declare readonly params: IParamsCourseObject;    

  public saving: boolean = false;
  public height: number = 300;
  public tags: Array<string> = ['TEST', 'CLASSWORK', 'HOMEWORK'];
  public tagSelected: string = '';
  private editorEmpty: boolean = true;
  private quillReady: boolean = false;

  @Ref('textEditor')
  textEditor!: any;

  get isHomework() {
    if(this.form) {
      return this.form.get('homework') ? this.form.get('homework').value : false;
    }
    return false;
  }

  get hasBeenResolvedByAnyStudent(){
    return this.params.section ? this.params.section.hasBeenResolvedByAnyStudent : false;
  }

  get title(): string {
    let title = this.section ? 'lesson.editTextTitle': 'courses.objects.createText';
    return String(this.$i18n.t(title));
  }

  get textContainerStyle() {
    return {
      height: `${this.height}px`
    }
  }

  get section(): Section {
    let section = this.params.section ? SectionsProvider.getSectionById(
      this.course.sections,
      this.params.section.sectionId
    ) : null;
    return section ? section.value : null;
  }

  get shouldArchiveCurrentHomeworks(){
    return this.section.text == this.form.text.value ? false : true;
  }

  init(name = '', homework = false, dueDate = '', lastDate: string = '', homeworkPoints: number = 100, homeworkWeight: number = 1) {
    this.form = EdFormModel([
      EdInputModel('name', name, { required, maxLength: maxLength(60)}),
      EdInputModel('homework', homework, {}, true),
      EdInputModel('dueDate', dueDate, {}),
      EdInputModel('lastDate', lastDate, {}),
      EdInputModel('homeworkPoints', homeworkPoints, { numeric, between: between(1, 1000)}, true),
      EdInputModel('homeworkWeight', homeworkWeight, { numeric, between: between(1, 1000)}, true)
    ]);
    if(homework && this.hasGrading){
      this.form.setValidator('homeworkPoints', 'required', required);
      this.form.setValidator('homeworkWeight', 'required', required);
    }
  }

  saveText() {
    let hasBeenResolvedByAnyStudent = this.params.section ? this.params.section.hasBeenResolvedByAnyStudent : false;
    if(hasBeenResolvedByAnyStudent && this.shouldArchiveCurrentHomeworks) {
      this.$EdModal.createConfirmationModal(
        {
          body: this.$i18n.t('courses.objects.confirmEdit'),
          action: async () => { this.doSaveText(); }
        }
      );
    } else {
      this.doSaveText();
    }
  }

  async doSaveText() {
    let data: any = this.section ? Object.assign({}, this.section) : {};
    try {
      this.saving = true;
      const text = JSON.stringify({delta: this.textEditor._data.quillEditor.getContents()});
      this.form.text = text;
      data.type = SECTION_TYPES.LESSON_TEXT.code,
      data.name = this.form.name.value,
      data.text = text,
      data.homework = this.form.homework.value,
      data.parentId = this.params.parentId,
      data.courseId = this.params.courseId,
      data.classId = this.params.classId,
      data.submissionDates = {
        dueSchoolDay: this.getSchoolDayIndex(this.form.dueDate.value),
        dueDate: this.form.dueDate.value,
        lastSchoolDay: this.getSchoolDayIndex(this.form.lastDate.value),
        lastDate: this.form.lastDate.value,
        ...this.dueDateInfo
      }
      data.archiveCurrentHomeworks = false;
      data.labelSections = this.tagSelected;
      data.component = this.componentSelected;
      if(this.hasGrading) {
        data.homeworkPoints = this.form.homeworkPoints.value,
        data.homeworkWeight = this.form.homeworkWeight.value
      }
      if(this.section) {
        data.archiveCurrentHomeworks = this.shouldArchiveCurrentHomeworks;
        await this.editObject(data);
      } else {
        await this.createObject(data);
      } 
      this.closeModal();
      await this.goToRefreshSectionsTree({
        sectionId: this.section.sectionId,
        parentId: this.params.parentId,
        courseId: this.params.courseId,
        classId: this.params.classId
      });

    } catch(err) {
      this.saving = false;
    }
  }

  setHeight() {
    const height = (window.innerHeight - 380) * 0.8;
    this.height = height > 300 ? height : 300;
  }

  closeModal() {
    this.$EdModal.closeModal();
  }

  requireHomeworkPoints(){
    if (this.form.homework.value && this.hasGrading){
      this.form.setValidator('homeworkPoints', 'required', required);
      this.form.setValidator('homeworkWeight', 'required', required);
    } else{
      this.form.removeValidator('homeworkPoints', 'required')
      this.form.removeValidator('homeworkWeight', 'required')
    }
  }

  setEditionMode() {
    let dates = this.section.submissionDates || {}; 

    this.tagSelected = this.section.labelSections;
    this.init(
      this.section.name,
      this.section.homework,
      this.getSchoolDayDate(dates.dueSchoolDay),
      this.getSchoolDayDate(dates.lastSchoolDay),
      this.section.homeworkPoints,
      this.section.homeworkWeight
    );
    this.setTextEditorWithFormData();
  }

  setTextEditorWithFormData(): void {
    if(this.textEditor && this.quillReady) {
      this.form.text = this.section.text;
      const textEditor: any =  this.textEditor._data.quillEditor;
      this.setTextEditorContent(textEditor, this.getDeltaFromText(this.section.text).delta);
    } else {
      setTimeout(() => {
        this.setTextEditorWithFormData();
      }, 200);
    }
  }

  getDeltaFromText(text: any): {delta: Delta} {
    if (typeof text === 'string' && text !== null) {
      try {
        return JSON.parse(text);
      } catch (error) {
        return { delta: new Delta() };
      }
    }
    return text;

  }

  textEditorEmpty(data) {
    this.editorEmpty = data;
  }

  initQuillFinished(){
    this.quillReady = true;
  }

  created() {
    this.setHeight();
    if(!this.section) {
      this.init();
      if(this.course.eduacComponents) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    } else {
      this.setEditionMode();
      if(this.components && this.section.component) {
        this.componentSelected = this.components.find(c => c.id == this.section.component.id);
      } else if(this.components) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    }
  }
}
